<template>
  <div v-if="companyData" class="app-header-container">
    <header class="app-header">
      <div class="navbar">
        <!-- Neuer Bereich für Logo und Firmenname -->
        <div class="navbar-brand">
          <!-- Lade-Indikator -->
          <div v-if="!companyData" class="placeholder-loading">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Wird geladen...</span>
            </div>
          </div>
          
          <!-- Firmenlogo und -name -->
          <template v-else>
            <!-- Super-Admin Logo -->
            <div v-if="isSuperAdmin" class="company-logo logo-md">
              <img src="@/assets/img/KE_logo.png" alt="Eder IT Solutions Logo" @error="handleLogoError" style="background-color: cadetblue;"/>
            </div>
            <!-- Normales Firmenlogo -->
            <CompanyLogo 
              v-else
              :company="companyData" 
              size="md"
              @error="handleLogoError" 
            />
            <h4 class="company-name">
              {{ companyName }}
            </h4>
          </template>
          
          <!-- Toggle Button für SideNav -->
          <button class="toggle-btn ms-3" @click="toggleSidebar" aria-label="Menü ein-/ausklappen">
            <i class="fas" :class="{'fa-bars': !sidebarCollapsed, 'fa-indent': sidebarCollapsed}"></i>
          </button>
        </div>
        
        <div class="navbar-center">
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input type="text" placeholder="Suchen..." class="search-input" v-model="searchQuery" @keyup.enter="handleSearch">
            <button class="search-close" v-if="searchQuery" @click="searchQuery = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <div class="navbar-right">
          <div class="nav-item header-actions">
            <template v-for="(item, index) in userDropdownMenu" :key="index">
              <!-- Dark Mode Toggle -->
              <button 
                v-if="item.action && item.id === 'darkmode'"
                @click="toggleDarkMode"
                class="header-action-btn"
                :title="isDarkMode ? item.toggleTitle.on : item.toggleTitle.off"
              >
                <i :class="isDarkMode ? item.toggleIcon.on : item.toggleIcon.off"></i>
                <span class="hide-sm">{{ isDarkMode ? item.toggleTitle.on : item.toggleTitle.off }}</span>
              </button>
              
              <!-- Navigation Links -->
              <router-link
                v-else-if="item.path"
                :to="item.path"
                class="header-action-btn"
                :title="item.title"
              >
                <i :class="item.icon"></i>
                <span class="hide-sm">{{ item.title }}</span>
              </router-link>
              
              <!-- Action Buttons (z.B. Logout) -->
              <button
                v-else-if="item.action"
                @click="handleAction(item.id)"
                class="header-action-btn"
                :title="item.title"
              >
                <i :class="item.icon"></i>
                <span class="hide-sm">{{ item.title }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
      
      <!-- Impersonation-Banner anzeigen, wenn aktiv -->
      <div class="impersonation-banner" v-if="isImpersonating">
        <div class="impersonation-message">
          <i class="fas fa-user-secret"></i>
          <span>Sie sind als <strong>{{ userName }}</strong> angemeldet.</span>
        </div>
        <button class="btn btn-sm btn-light" @click="stopImpersonating">
          <i class="fas fa-sign-out-alt"></i>
          Zurück zu Ihrem Konto
        </button>
      </div>
    </header>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { userDropdownMenu, firmNavigation, superAdminNavigation } from '@/config/navigation';
import CompanyLogo from '@/components/ui/CompanyLogo.vue';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';

const props = defineProps({
  sidebarCollapsed: {
    type: Boolean,
    default: false
  }
});

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const companyStore = useCompanyStore();

// Reaktive Datenzustände
const searchQuery = ref('');
const notificationsOpen = ref(false);
const userMenuOpen = ref(false);
const isDarkMode = ref(false);
const breadcrumbs = ref([]);
const showImpersonateModal = ref(false);

// const notifications = ref([
//   {
//     id: 1,
//     type: 'info',
//     title: 'Neuer Bewerber',
//     message: 'Ein neuer Bewerber hat sich für den Frontend Test registriert.',
//     time: new Date(Date.now() - 1800000), // 30 Minuten zuvor
//     read: false
//   },
//   {
//     id: 2,
//     type: 'success',
//     title: 'Test abgeschlossen',
//     message: 'Thomas Schmidt hat den React Test erfolgreich abgeschlossen.',
//     time: new Date(Date.now() - 5400000), // 1.5 Stunden zuvor
//     read: false
//   },
//   {
//     id: 3,
//     type: 'warning',
//     title: 'Lizenz läuft ab',
//     message: 'Ihre Premium-Lizenz läuft in 3 Tagen ab.',
//     time: new Date(Date.now() - 86400000), // 1 Tag zuvor
//     read: true
//   }
// ]);

const user = computed(() => authStore.user);
const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isImpersonating = computed(() => authStore.isImpersonating);
const companyData = computed(() => companyStore.currentCompany);


const userAvatar = computed(() => {
  return user.value && user.value.avatar ? user.value.avatar : null;
});

const userName = computed(() => {
  return user.value && user.value.name ? user.value.name : 'Benutzer';
});

const userEmail = computed(() => {
  return user.value && user.value.email ? user.value.email : '';
});

const userInitials = computed(() => {
  if (!user.value || !user.value.name) return '?';
  
  const nameParts = user.value.name.split(' ');
  if (nameParts.length >= 2) {
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  } else if (nameParts[0] && nameParts[0].length >= 2) {
    return nameParts[0].substring(0, 2).toUpperCase();
  } else {
    return '?';
  }
});

const companyName = computed(() => {
  if (isSuperAdmin.value) {
    return 'Eder IT Solutions';
  }

  return companyData.value ? companyData.value.name : 'Firma wird geladen...';
});

// const unreadNotifications = computed(() => {
//   return notifications.value.filter(n => !n.read).length;
// });


// Methoden
const toggleSidebar = () => {
  emit('toggle-sidebar');
};

const emit = defineEmits(['toggle-sidebar']);

// const toggleNotifications = () => {
//   notificationsOpen.value = !notificationsOpen.value;
//   userMenuOpen.value = false;
// };

const toggleUserMenu = (event) => {
  event.stopPropagation(); // Verhindert Bubble-up
  userMenuOpen.value = !userMenuOpen.value;
  notificationsOpen.value = false;
};

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  document.body.classList.toggle('dark-mode', isDarkMode.value);
  localStorage.setItem('darkMode', isDarkMode.value ? 'true' : 'false');
};

const handleAction = async (actionId) => {
  switch (actionId) {
    case 'logout':
      try {
        await authStore.logout();
        router.push('/login');
      } catch (error) {
        console.error('Fehler beim Abmelden:', error);
      }
      break;
    case 'impersonate':
      showImpersonateModal.value = true;
      break;
    case 'stop-impersonate':
      await stopImpersonating();
      break;
  }
  userMenuOpen.value = false;
};

const stopImpersonating = async () => {
  try {
    await authStore.stopImpersonating();
    router.push('/admin/dashboard');
  } catch (error) {
    console.error('Fehler beim Beenden der Impersonation:', error);
  }
};

const handleSearch = () => {
  if (!searchQuery.value.trim()) return;
  
  // Hier könnte später die Suchfunktion implementiert werden
  console.log('Suche nach:', searchQuery.value);
  searchQuery.value = '';
};

// const markAllAsRead = () => {
//   notifications.value.forEach(notification => {
//     notification.read = true;
//   });
// };

// const formatTime = (time) => {
//   const now = new Date();
//   const diff = now - time;
  
//   if (diff < 60000) {
//     return 'Gerade eben';
//   } else if (diff < 3600000) {
//     return `Vor ${Math.floor(diff / 60000)} Minuten`;
//   } else if (diff < 86400000) {
//     return `Vor ${Math.floor(diff / 3600000)} Stunden`;
//   } else {
//     return `Am ${time.toLocaleDateString()}`;
//   }
// };

// const getNotificationIcon = (type) => {
//   switch (type) {
//     case 'info': return 'fas fa-info-circle';
//     case 'success': return 'fas fa-check-circle';
//     case 'warning': return 'fas fa-exclamation-triangle';
//     case 'error': return 'fas fa-times-circle';
//     default: return 'fas fa-bell';
//   }
// };

const handleLogoError = (e) => {
  e.target.src = '@/assets/img/company-placeholder.png';
};

// Click-Outside Handler
const handleOutsideClick = (event) => {
  const dropdown = document.querySelector('.user-dropdown');
  const button = document.querySelector('.user-button');
  
  if (dropdown && !dropdown.contains(event.target) && !button.contains(event.target)) {
    userMenuOpen.value = false;
  }
};

// Lifecycle Hooks
onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
  
  // Dark Mode aus localStorage laden
  const savedDarkMode = localStorage.getItem('darkMode');
  if (savedDarkMode === 'true') {
    isDarkMode.value = true;
    document.body.classList.add('dark-mode');
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>

<style lang="scss" scoped>
.app-header-container {
  width: 100%;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.app-header {
  background-color: #fff;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.navbar {
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  justify-content: space-between;
  border-bottom: 1px solid #eaeaea;
}

.navbar-brand {
  display: flex;
  align-items: center;
  min-width: 200px;
}

.navbar-center {
  flex-grow: 1;
  max-width: 600px;
  margin: 0 auto;
}

.navbar-right {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1100;
}

.company-logo {
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  margin-right: 10px;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  &.logo-md {
    width: 40px;
    height: 40px;
  }
}

.company-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.toggle-btn {
  background: none;
  border: none;
  color: #555;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
  }
}

.search-box {
  position: relative;
  width: 100%;
}

.search-input {
  background-color: #f5f5f5;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  width: 100%;
  transition: all 0.3s;
  
  &:focus {
    background-color: #fff;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
    outline: none;
  }
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
}

.search-close {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 0;
}

.nav-item {
  position: relative;
  margin-left: 20px;
}

.btn-icon {
  background: none;
  border: none;
  position: relative;
  font-size: 1.2rem;
  padding: 0.5rem;
  border-radius: 50%;
  color: #555;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  .badge {
    position: absolute;
    top: 0;
    right: 0;
    background-color: var(--primary-color);
    color: white;
    font-size: 0.7rem;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.nav-item.user-dropdown {
  position: relative;
  margin-left: 20px;
  z-index: 1100;
}

.user-button {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1101;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  .user-name {
    font-weight: 500;
    margin-right: 8px;
  }
  
  .dropdown-arrow {
    transition: transform 0.2s ease;
  }
}

.dropdown-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 5px);
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 280px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1102;
  border: 1px solid rgba(0, 0, 0, 0.1);
  
  &.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #333;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  z-index: 1103;
  
  i {
    margin-right: 12px;
    min-width: 20px;
    text-align: center;
    font-size: 16px;
    color: #666;
  }
  
  &:hover {
    background-color: rgba(var(--primary-color-rgb), 0.1);
    color: var(--primary-color);
    
    i {
      color: var(--primary-color);
    }
  }
  
  &:active {
    background-color: rgba(var(--primary-color-rgb), 0.2);
  }
}

.dropdown-header {
  padding: 1rem;
  border-bottom: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h5 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .btn-link {
    background: none;
    border: none;
    color: var(--primary-color);
    font-size: 0.875rem;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.user-header {
  display: flex;
  align-items: center;
  padding: 1rem;
}

.user-info {
  margin-left: 12px;
  
  .user-name {
    font-weight: 600;
    font-size: 1rem;
    margin: 0;
  }
  
  .user-email {
    font-size: 0.875rem;
    color: #666;
    margin: 0;
  }
}

.dropdown-content {
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-footer {
  padding: 1rem;
  border-top: 1px solid #eaeaea;
  text-align: center;
  
  .btn-link {
    color: var(--primary-color);
    text-decoration: none;
    font-size: 0.875rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.notification-item {
  display: flex;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eaeaea;
  transition: background-color 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f5f7fa;
  }
  
  &.unread {
    background-color: rgba(var(--primary-color-rgb), 0.05);
  }
}

.notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  
  &.info {
    background-color: #e1f5fe;
    color: #039be5;
  }
  
  &.success {
    background-color: #e8f5e9;
    color: #43a047;
  }
  
  &.warning {
    background-color: #fff8e1;
    color: #ffb300;
  }
  
  &.error {
    background-color: #ffebee;
    color: #e53935;
  }
}

.notification-content {
  flex-grow: 1;
}

.notification-title {
  font-weight: 600;
  margin-bottom: 2px;
}

.notification-text {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 4px;
}

.notification-time {
  font-size: 0.75rem;
  color: #888;
}

.empty-state {
  padding: 2rem;
  text-align: center;
  color: #888;
  
  i {
    font-size: 2rem;
    color: #ddd;
    margin-bottom: 1rem;
  }
  
  p {
    margin: 0;
  }
}

.hide-sm {
  @media (max-width: 768px) {
    display: none;
  }
}

.impersonation-banner {
  background-color: #fff3cd;
  color: #856404;
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  margin-left: 999px;
  
  .impersonation-message {
    display: flex;
    align-items: center;
    
    i {
      margin-right: 8px;
    }
  }
  
  .btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
    margin-left: 1rem;
  }
}

.placeholder-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 40px;
}

/* Anpassungen Dark Mode */
:deep(.dark-mode) {
  .app-header {
    background-color: #1e2125;
    border-bottom-color: #343a40;
  }
  
  .navbar {
    border-bottom-color: #343a40;
  }
  
  .company-logo {
    background-color: #343a40;
  }
  
  .company-name, .user-name, .dropdown-arrow, .toggle-btn {
    color: #e4e6eb;
  }
  
  .search-input {
    background-color: #343a40;
    color: #e4e6eb;
    
    &:focus {
      background-color: #2c3035;
    }
  }
  
  .search-icon, .search-close {
    color: #adb5bd;
  }
  
  .btn-icon {
    color: #e4e6eb;
    
    &:hover {
      background-color: #343a40;
    }
  }
  
  .dropdown-menu {
    background-color: #2c3035;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  .dropdown-header, .dropdown-footer {
    border-color: #343a40;
  }
  
  .dropdown-item {
    color: #e4e6eb;
    
    &:hover {
      background-color: #343a40;
    }
  }
  
  .notification-item {
    border-color: #343a40;
    
    &:hover {
      background-color: #343a40;
    }
    
    &.unread {
      background-color: rgba(var(--primary-color-rgb), 0.15);
    }
  }
  
  .notification-text {
    color: #adb5bd;
  }
  
  .notification-time {
    color: #6c757d;
  }
  
  .empty-state {
    color: #adb5bd;
    
    i {
      color: #495057;
    }
  }
}
</style> 